/**
 * CSV export utilities for client-side CSV generation.
 */

/**
 * Escapes a value for CSV format.
 * Wraps in quotes if contains comma, newline, or quote.
 * Doubles any existing quotes.
 */
function escapeCSV(value: string | number | boolean | undefined | null): string {
  if (value === null || value === undefined) {
    return ''
  }
  const str = String(value)
  // If contains comma, newline, or double quote, wrap in quotes and escape quotes
  if (str.includes(',') || str.includes('\n') || str.includes('"')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Converts an array of objects to CSV string.
 * @param data - Array of objects to convert
 * @param columns - Array of column definitions with key (string property name) or accessor function, and header
 * @returns CSV string
 */
export function toCSV<T>(
  data: T[],
  columns: { key: keyof T | ((row: T) => string | number | boolean | undefined | null); header: string }[]
): string {
  const headers = columns.map((col) => escapeCSV(col.header)).join(',')

  const rows = data.map((row) =>
    columns
      .map((col) => {
        const value = typeof col.key === 'function' ? col.key(row) : row[col.key]
        return escapeCSV(value as string | number | boolean | undefined | null)
      })
      .join(',')
  )

  return [headers, ...rows].join('\n')
}

/**
 * Downloads a CSV string as a file.
 * @param csv - CSV string content
 * @param filename - Name of the downloaded file (should end with .csv)
 */
export function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
